import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  AreaChart, Area, CartesianGrid,
} from "recharts";
import {
  Briefcase, DollarSign, CheckCircle2, AlertCircle, Plus, Pencil, Trash2,
  Download, Save, X, Users, TrendingUp, BarChart3, Globe, PencilLine,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const EMPTY_FORM = {
  customer_name: "", customer_phone: "", customer_email: "", customer_address: "",
  service_name: "", date: "", total_price: "", notes: "", paid: false,
};

function prettyDate(d) {
  if (!d) return "—";
  return new Date(d + "T00:00:00").toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function money(n) {
  if (n == null) return "—";
  return `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

function cssVar(name, fallback) {
  if (typeof window === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v ? `hsl(${v})` : fallback;
}

export const JobLog = () => {
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");

  const colors = useMemo(() => ({
    primary: cssVar("--primary", "hsl(142 40% 25%)"),
    accent: cssVar("--accent", "hsl(30 40% 60%)"),
    muted: cssVar("--muted-foreground", "hsl(140 10% 40%)"),
  }), [dialogOpen]); // recompute when UI interacts (theme-safe enough)

  const load = () => {
    Promise.all([api.get("/admin/jobs"), api.get("/admin/analytics")])
      .then(([j, a]) => { setJobs(j.data); setStats(a.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditingId(null); setForm(EMPTY_FORM); setDialogOpen(true); };
  const openEdit = (b) => {
    setEditingId(b.id);
    setForm({
      customer_name: b.customer?.name || "",
      customer_phone: b.customer?.phone || "",
      customer_email: b.customer?.email || "",
      customer_address: b.customer?.address || "",
      service_name: b.service_name || "",
      date: b.date || "",
      total_price: b.total_price ?? "",
      notes: b.notes || "",
      paid: !!b.paid,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.customer_name.trim() || !form.service_name.trim()) {
      toast.error("Customer name and service are required.");
      return;
    }
    setSaving(true);
    const body = {
      customer_name: form.customer_name,
      customer_phone: form.customer_phone,
      customer_email: form.customer_email,
      customer_address: form.customer_address,
      service_name: form.service_name,
      date: form.date || null,
      total_price: form.total_price === "" ? null : Number(form.total_price),
      notes: form.notes || null,
      paid: form.paid,
    };
    try {
      if (editingId) await api.patch(`/admin/jobs/${editingId}`, body);
      else await api.post("/admin/jobs", body);
      toast.success(editingId ? "Job updated" : "Job added");
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const togglePaid = async (b) => {
    try {
      await api.patch(`/admin/jobs/${b.id}`, { paid: !b.paid });
      setJobs((prev) => prev.map((x) => (x.id === b.id ? { ...x, paid: !b.paid } : x)));
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete this job for ${b.customer?.name || "this customer"}? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/jobs/${b.id}`);
      toast.success("Job removed");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const exportCsv = () => {
    const cols = ["Date", "Customer", "Email", "Phone", "Address", "Service", "Price", "Paid", "Source", "Notes"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = filtered.map((b) => [
      b.date || "", b.customer?.name || "", b.customer?.email || "", b.customer?.phone || "",
      b.customer?.address || "", b.service_name || "", b.total_price ?? "",
      b.paid ? "Paid" : "Unpaid", b.manual ? "Manual" : "Website", (b.notes || "").replace(/\n/g, " "),
    ].map(esc).join(","));
    const csv = [cols.map(esc).join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `job-log-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return jobs;
    return jobs.filter((b) =>
      [b.customer?.name, b.customer?.email, b.customer?.phone, b.customer?.address, b.service_name, b.notes]
        .some((v) => (v || "").toLowerCase().includes(q))
    );
  }, [jobs, query]);

  const STAT_CARDS = stats ? [
    { key: "jobs", icon: Briefcase, label: "Jobs completed", value: stats.total_jobs, testid: "stat-total-jobs" },
    { key: "rev", icon: DollarSign, label: "Total revenue", value: money(stats.total_revenue), testid: "stat-total-revenue" },
    { key: "paid", icon: CheckCircle2, label: "Collected (paid)", value: money(stats.paid_total), testid: "stat-paid-total" },
    { key: "out", icon: AlertCircle, label: "Outstanding (unpaid)", value: money(stats.outstanding_total), testid: "stat-outstanding", warn: stats.outstanding_total > 0 },
  ] : [];

  return (
    <div data-testid="joblog-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-primary" />
          <h2 className="font-display font-bold text-xl">Job log &amp; insights</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="export-csv-button" onClick={exportCsv} disabled={filtered.length === 0}
            className="inline-flex items-center gap-2 text-sm font-bold px-4 py-2.5 rounded border border-border hover:border-primary hover:text-primary transition-colors disabled:opacity-40">
            <Download className="w-4 h-4" /> Download CSV
          </button>
          <button data-testid="add-job-button" onClick={openAdd}
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground text-sm font-bold px-4 py-2.5 rounded transition-transform active:scale-95 hover:bg-primary/90">
            <Plus className="w-4 h-4" /> Add a job
          </button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {STAT_CARDS.map((s) => (
          <div key={s.key} data-testid={s.testid} className="bg-card border border-border rounded p-5">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">
              <s.icon className={`w-4 h-4 ${s.warn ? "text-destructive" : "text-primary"}`} /> {s.label}
            </div>
            <div className={`text-3xl font-display font-black mt-2 ${s.warn ? "text-destructive" : "text-primary"}`}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Charts */}
      {stats && (stats.most_requested.length > 0 || stats.revenue_over_time.length > 0) && (
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-card border border-border rounded p-6" data-testid="chart-most-requested">
            <div className="flex items-center gap-2 mb-4"><TrendingUp className="w-4 h-4 text-primary" /><h3 className="font-display font-bold">Most-requested services</h3></div>
            {stats.most_requested.length === 0 ? <p className="text-sm text-muted-foreground">No data yet.</p> : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={stats.most_requested} layout="vertical" margin={{ left: 10, right: 20 }}>
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} stroke={colors.muted} />
                  <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 12 }} stroke={colors.muted} />
                  <Tooltip cursor={{ fill: "rgba(0,0,0,0.04)" }} />
                  <Bar dataKey="count" fill={colors.primary} radius={[0, 4, 4, 0]} name="Jobs" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="bg-card border border-border rounded p-6" data-testid="chart-revenue-over-time">
            <div className="flex items-center gap-2 mb-4"><DollarSign className="w-4 h-4 text-primary" /><h3 className="font-display font-bold">Revenue over time</h3></div>
            {stats.revenue_over_time.length === 0 ? <p className="text-sm text-muted-foreground">No data yet.</p> : (
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={stats.revenue_over_time} margin={{ left: 0, right: 10, top: 5 }}>
                  <defs>
                    <linearGradient id="revfill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={colors.primary} stopOpacity={0.35} />
                      <stop offset="100%" stopColor={colors.primary} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke={colors.muted} />
                  <YAxis tick={{ fontSize: 12 }} stroke={colors.muted} />
                  <Tooltip formatter={(v) => money(v)} />
                  <Area type="monotone" dataKey="revenue" stroke={colors.primary} strokeWidth={2} fill="url(#revfill)" name="Revenue" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      )}

      {/* Top customers */}
      {stats && stats.top_customers.length > 0 && (
        <div className="bg-card border border-border rounded p-6 mb-8" data-testid="top-customers">
          <div className="flex items-center gap-2 mb-4"><Users className="w-4 h-4 text-primary" /><h3 className="font-display font-bold">Top customers</h3></div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {stats.top_customers.map((c, i) => (
              <div key={i} data-testid={`top-customer-${i}`} className="rounded border border-border p-3">
                <div className="font-semibold text-sm truncate">{c.name}</div>
                {c.email && <div className="text-xs text-muted-foreground truncate">{c.email}</div>}
                <div className="text-xs text-primary font-bold mt-1">{c.jobs} job{c.jobs !== 1 ? "s" : ""} · {money(c.revenue)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Spreadsheet */}
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="font-display font-bold text-lg">All completed jobs</h3>
        <Input data-testid="job-search-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search jobs…" className="max-w-xs" />
      </div>

      {loading ? (
        <div className="bg-card border border-border rounded p-12 text-center text-muted-foreground">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-card border border-border rounded p-12 text-center text-muted-foreground" data-testid="no-jobs">
          No completed jobs yet. Mark a booking "completed" or add one manually to start tracking.
        </div>
      ) : (
        <div className="bg-card border border-border rounded overflow-x-auto" data-testid="job-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Service</th>
                <th className="px-4 py-3 text-right">Price</th>
                <th className="px-4 py-3">Payment</th>
                <th className="px-4 py-3">Source</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((b) => (
                <tr key={b.id} data-testid={`job-row-${b.id}`} className="border-b border-border last:border-0 hover:bg-secondary/40">
                  <td className="px-4 py-3 whitespace-nowrap">{prettyDate(b.date)}</td>
                  <td className="px-4 py-3">
                    <div className="font-semibold">{b.customer?.name}</div>
                    <div className="text-xs text-muted-foreground">{b.customer?.phone || b.customer?.email}</div>
                  </td>
                  <td className="px-4 py-3">{b.service_name}</td>
                  <td className="px-4 py-3 text-right font-semibold">{money(b.total_price)}</td>
                  <td className="px-4 py-3">
                    <button data-testid={`toggle-paid-${b.id}`} onClick={() => togglePaid(b)}
                      className={`inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full border transition-colors ${b.paid ? "bg-primary/10 text-primary border-primary/30" : "bg-destructive/10 text-destructive border-destructive/30"}`}>
                      {b.paid ? <><CheckCircle2 className="w-3 h-3" /> Paid</> : <><AlertCircle className="w-3 h-3" /> Unpaid</>}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-xs font-medium ${b.manual ? "text-primary" : "text-muted-foreground"}`}>
                      {b.manual ? <><PencilLine className="w-3 h-3" /> Manual</> : <><Globe className="w-3 h-3" /> Website</>}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button data-testid={`edit-job-${b.id}`} onClick={() => openEdit(b)} className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1.5 rounded border border-border hover:border-primary hover:text-primary transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
                      <button data-testid={`delete-job-${b.id}`} onClick={() => remove(b)} className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1.5 rounded border border-border text-destructive hover:border-destructive transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add / edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="job-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editingId ? "Edit job" : "Add a job"}</DialogTitle>
            <DialogDescription>{editingId ? "Update this job's details." : "Record a job you booked off the website."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Customer name *</label>
                <Input data-testid="job-name-input" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="mt-1.5" /></div>
              <div><label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Phone</label>
                <Input data-testid="job-phone-input" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Email</label>
                <Input data-testid="job-email-input" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className="mt-1.5" /></div>
              <div><label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Address</label>
                <Input data-testid="job-address-input" value={form.customer_address} onChange={(e) => setForm({ ...form, customer_address: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-1"><label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Service *</label>
                <Input data-testid="job-service-input" value={form.service_name} onChange={(e) => setForm({ ...form, service_name: e.target.value })} placeholder="Lawn Mowing" className="mt-1.5" /></div>
              <div><label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Date</label>
                <Input data-testid="job-date-input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5" /></div>
              <div><label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Price ($)</label>
                <Input data-testid="job-price-input" type="number" value={form.total_price} onChange={(e) => setForm({ ...form, total_price: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div><label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Notes</label>
              <Textarea data-testid="job-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5 min-h-20" /></div>
            <label className="flex items-center gap-2 cursor-pointer rounded border border-border p-3 bg-background/50">
              <input type="checkbox" data-testid="job-paid-input" checked={form.paid} onChange={(e) => setForm({ ...form, paid: e.target.checked })} className="w-4 h-4 accent-[hsl(var(--primary))]" />
              <span className="text-sm font-bold">Marked as paid</span>
            </label>
          </div>
          <DialogFooter>
            <button onClick={() => setDialogOpen(false)} className="inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded border border-border hover:border-primary"><X className="w-4 h-4" /> Cancel</button>
            <button data-testid="save-job-button" onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground text-sm font-bold px-5 py-2 rounded hover:bg-primary/90 disabled:opacity-40"><Save className="w-4 h-4" /> {saving ? "Saving…" : "Save job"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
